import { useEffect } from 'react';

let timerAudioUnlocked = false;
let sharedAudioContext: AudioContext | null = null;

/** Unlock browser audio playback after a user gesture (autoplay policy). */
export function unlockTimerAudio(): void {
  if (timerAudioUnlocked || typeof window === 'undefined') return;

  const AudioCtx =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;

  if (AudioCtx) {
    try {
      sharedAudioContext = sharedAudioContext ?? new AudioCtx();
      void sharedAudioContext.resume().then(() => {
        timerAudioUnlocked = true;
      });
      return;
    } catch {
      /* fall through */
    }
  }

  // Allow clip playback attempts even when AudioContext is unavailable.
  timerAudioUnlocked = true;
}

/** Reset unlock state (for tests). */
export function resetTimerAudioUnlockForTests(): void {
  timerAudioUnlocked = false;
  void sharedAudioContext?.close();
  sharedAudioContext = null;
}

/**
 * One-shot unlock on pointer or keydown. Used by {@link TimerAudio} on timing scenes.
 */
export function useTimerAudioUnlock(): void {
  useEffect(() => {
    const unlock = () => unlockTimerAudio();
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, []);
}
